import type { StudentSummary } from '@school-erp/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';

interface Thread {
  id: string;
  subject: string;
  studentName: string;
  lastMessageAt: string;
  lastMessagePreview: string;
  lastSenderName: string | null;
  unread: boolean;
  participants: string[];
}
interface Conversation {
  id: string;
  subject: string;
  studentName: string;
  participants: string[];
  messages: { id: string; senderName: string; mine: boolean; body: string; at: string }[];
}
interface Recipient {
  userId: string;
  name: string;
  relation: string;
}

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** Messages between home and school about a student: a list of conversations and the open one. */
export function MessagesPage() {
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const threads = useQuery({
    queryKey: ['messages', 'threads'],
    queryFn: () => apiList<Thread>('/me/messages/threads', { query: { pageSize: 50 } }),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['messages'] });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('msg.title')}</h1>
        <button type="button" className={primaryButton} onClick={() => setComposing((v) => !v)}>
          {t('msg.new')}
        </button>
      </div>
      {composing && (
        <Composer
          onSent={(id) => {
            setComposing(false);
            setOpenId(id);
            refresh();
          }}
        />
      )}
      <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
        <section aria-label={t('msg.threads')} className="space-y-2">
          {threads.isPending && <p role="status">{t('common.loading')}</p>}
          {threads.isError && <Notice kind="error">{errorMessage(threads.error)}</Notice>}
          {threads.data?.data.length === 0 && <p className="text-slate-600">{t('msg.empty')}</p>}
          <ul className="space-y-2">
            {threads.data?.data.map((th) => (
              <li key={th.id}>
                <button
                  type="button"
                  onClick={() => setOpenId(th.id)}
                  aria-current={openId === th.id ? 'true' : undefined}
                  className={`block w-full rounded-lg border p-3 text-left ${openId === th.id ? 'border-brand-700 bg-brand-50' : 'border-slate-200 bg-white'}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className={`text-sm ${th.unread ? 'font-bold' : 'font-medium'}`}>
                      {th.subject}
                    </span>
                    {th.unread && (
                      <span className="rounded-full bg-brand-700 px-2 py-0.5 text-xs text-white">
                        {t('msg.unread')}
                      </span>
                    )}
                  </span>
                  <span className="block text-xs text-slate-600">
                    {th.studentName} · {th.participants.join(', ')}
                  </span>
                  <span className="mt-1 block truncate text-sm text-slate-700">
                    {th.lastMessagePreview}
                  </span>
                  <span className="block text-xs text-slate-500">{when(th.lastMessageAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
        <section aria-label={t('msg.conversation')}>
          {openId ? (
            <ConversationView id={openId} onChanged={refresh} />
          ) : (
            <p className="text-slate-600">{t('msg.pick')}</p>
          )}
        </section>
      </div>
    </div>
  );
}

function ConversationView({ id, onChanged }: { id: string; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const conversation = useQuery({
    queryKey: ['messages', 'thread', id],
    queryFn: () => api<Conversation>(`/me/messages/threads/${id}`),
  });
  const reply = useMutation({
    mutationFn: () =>
      api<Conversation>(`/me/messages/threads/${id}/reply`, { method: 'POST', body: { body } }),
    onSuccess: (data) => {
      setBody('');
      queryClient.setQueryData(['messages', 'thread', id], data);
      onChanged();
    },
  });
  if (conversation.isPending) return <p role="status">{t('common.loading')}</p>;
  if (conversation.isError) return <Notice kind="error">{errorMessage(conversation.error)}</Notice>;
  const c = conversation.data;
  return (
    <div className="space-y-3">
      <header>
        <h2 className="text-lg font-semibold">{c.subject}</h2>
        <p className="text-sm text-slate-600">
          {c.studentName} · {c.participants.join(', ')}
        </p>
      </header>
      <ol className="space-y-2" aria-label={t('msg.messages')}>
        {c.messages.map((m) => (
          <li
            key={m.id}
            className={`max-w-[85%] rounded-lg p-3 text-sm ${m.mine ? 'ml-auto bg-brand-50' : 'bg-white shadow-sm ring-1 ring-slate-200'}`}
          >
            <p className="text-xs font-medium text-slate-600">
              {m.mine ? t('msg.you') : m.senderName} · {when(m.at)}
            </p>
            <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
          </li>
        ))}
      </ol>
      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim()) reply.mutate();
        }}
      >
        <label htmlFor="msg-reply" className="block text-sm font-medium">
          {t('msg.reply')}
        </label>
        <textarea
          id="msg-reply"
          rows={3}
          className="block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        {reply.isError && <Notice kind="error">{errorMessage(reply.error)}</Notice>}
        <button type="submit" className={primaryButton} disabled={!body.trim() || reply.isPending}>
          {t('msg.send')}
        </button>
      </form>
    </div>
  );
}

function Composer({ onSent }: { onSent: (threadId: string) => void }) {
  const { can } = useAuth();
  const homeSide = can('portal.parent');
  const [search, setSearch] = useState('');
  const [student, setStudent] = useState<{ id: string; name: string } | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const children = useQuery({
    queryKey: ['me', 'children'],
    queryFn: () => api<StudentSummary[]>('/me/children'),
    enabled: homeSide,
  });
  const found = useQuery({
    queryKey: ['messages', 'students', search],
    queryFn: () =>
      apiList<StudentSummary>('/students', { query: { q: search, pageSize: 6 } }).then(
        (r) => r.data,
      ),
    enabled: !homeSide && search.trim().length >= 2 && !student,
  });
  const recipients = useQuery({
    queryKey: ['messages', 'recipients', student?.id],
    queryFn: () =>
      api<Recipient[]>('/me/messages/recipients', { query: { studentId: student!.id } }),
    enabled: !!student,
  });
  const send = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/me/messages/threads', {
        method: 'POST',
        body: { studentId: student!.id, subject, body, recipientIds: chosen },
      }),
    onSuccess: (thread) => onSent(thread.id),
  });
  const toggle = (id: string) =>
    setChosen((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );

  return (
    <form
      aria-label={t('msg.new')}
      className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        send.mutate();
      }}
    >
      {homeSide ? (
        <div>
          <label htmlFor="msg-child" className="block text-sm font-medium">
            {t('msg.about')}
          </label>
          <select
            id="msg-child"
            className={selectClass}
            value={student?.id ?? ''}
            onChange={(e) => {
              const c = children.data?.find((x) => x.id === e.target.value);
              setStudent(c ? { id: c.id, name: `${c.firstName} ${c.lastName}` } : null);
              setChosen([]);
            }}
          >
            <option value="">{t('hw.choose')}</option>
            {children.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.firstName} {c.lastName}
              </option>
            ))}
          </select>
        </div>
      ) : student ? (
        <p className="text-sm">
          {t('msg.about')}: <strong>{student.name}</strong>{' '}
          <button
            type="button"
            className="text-brand-700 underline"
            onClick={() => {
              setStudent(null);
              setChosen([]);
            }}
          >
            {t('msg.change')}
          </button>
        </p>
      ) : (
        <div>
          <TextField
            label={t('msg.findStudent')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ul className="mt-2 space-y-1">
            {found.data?.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={secondaryButton}
                  onClick={() => setStudent({ id: s.id, name: `${s.firstName} ${s.lastName}` })}
                >
                  {s.firstName} {s.lastName} · {s.admissionNumber}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {student && (
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium">{t('msg.to')}</legend>
          {recipients.isPending && <p role="status">{t('common.loading')}</p>}
          {recipients.data?.length === 0 && (
            <p className="text-sm text-slate-600">{t('msg.noRecipients')}</p>
          )}
          {recipients.data?.map((r) => (
            <label key={r.userId} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={chosen.includes(r.userId)}
                onChange={() => toggle(r.userId)}
              />
              {r.name} <span className="text-slate-600">({r.relation})</span>
            </label>
          ))}
        </fieldset>
      )}
      <TextField
        label={t('msg.subject')}
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
      />
      <div>
        <label htmlFor="msg-body" className="block text-sm font-medium">
          {t('msg.body')}
        </label>
        <textarea
          id="msg-body"
          rows={4}
          className="mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>
      {send.isError && <Notice kind="error">{errorMessage(send.error)}</Notice>}
      <button
        type="submit"
        className={primaryButton}
        disabled={
          !student || chosen.length === 0 || !subject.trim() || !body.trim() || send.isPending
        }
      >
        {t('msg.send')}
      </button>
    </form>
  );
}
